import {
  renderCachedUserAvatarUrl,
  revokeCachedUserAvatarObjectUrl,
  type UserAvatarCacheService,
  UserProvider,
} from '@blocksuite/affine-shared/services';
import { cssVarV2 } from '@blocksuite/affine-shared/theme';
import { agentPointerLabel } from '@blocksuite/affine-shared/utils';
import { MultiCursorDuotoneIcon } from '@blocksuite/icons/lit';
import type { BlockStdScope } from '@blocksuite/std';
import type {
  AgentPointer,
  AgentPointerTarget,
  UserInfo,
} from '@blocksuite/store';
import { html, nothing } from 'lit';
import { repeat } from 'lit/directives/repeat.js';
import { styleMap } from 'lit/directives/style-map.js';

import {
  type AgentPointerPosition,
  moveAgentPointer,
} from './agent-pointer-motion';

export function createAgentPointerOwnerResolver(std: BlockStdScope) {
  const requested = new Set<string>();
  return (ownerUserId: string): UserInfo | null => {
    const users = std.getOptional(UserProvider);
    if (!users) return null;
    if (!requested.has(ownerUserId)) {
      requested.add(ownerUserId);
      users.revalidateUserInfo(ownerUserId);
    }
    const owner = users.userInfo$(ownerUserId).value;
    return owner && !owner.removed && owner.name
      ? { id: owner.id, name: owner.name, avatar: owner.avatar }
      : null;
  };
}

/**
 * A run that ends leaves its pointer where it last worked this long, after it
 * has gone the rest of its way: a change confirmed at the end of a run is seen.
 */
export const ENDED_RUN_POINTER_MS = 1500;

/** Below this distance (in canvas units) a target stayed where it was. */
const MOVED_TARGET = 0.5;

/** The target moved: a new way, which ends what the pointer carries now. */
function moved(from: AgentPointerPosition, to: AgentPointerPosition): boolean {
  return Math.hypot(to.x - from.x, to.y - from.y) >= MOVED_TARGET;
}

interface DrawnAgentPointer {
  pointer: AgentPointer;
  owner: UserInfo | null;
  position: AgentPointerPosition;
  /** What the pointer goes to now, and where that is on screen. */
  heading: AgentPointerTarget;
  target: AgentPointerPosition;
  /** What it goes to after, in the order the change made them. */
  route: AgentPointerTarget[];
  /** Stops the way; what it carries ends too, unless the next way takes it on. */
  stop(keepCarry?: boolean): void;
  /** What it carries on this way, if anything. */
  carrying?: AgentPointerTarget;
  /** Its way is gone: the pointer stands at its last object. */
  done: boolean;
  /** Its run ended: it leaves once it stood at its last object a moment. */
  ended: boolean;
  leave?: number;
}

/** What a pointer carries on its way to a target, e.g. a database row it drags. */
export interface AgentPointerCarry {
  /** Share of the way done, 0 to 1. */
  moved(progress: number): void;
  /** Reached, or the way was given up. */
  arrived(): void;
}

export class AgentPointerDisplay {
  private readonly drawn = new Map<string, DrawnAgentPointer>();

  constructor(
    private readonly input: {
      pointers(): ReadonlyMap<string, AgentPointer>;
      owner(ownerUserId: string): UserInfo | null;
      position(target: AgentPointerTarget): AgentPointerPosition | null;
      /** What the pointer carries on its way to `target`. */
      carry?(target: AgentPointerTarget): AgentPointerCarry | null;
      scale(): number;
      changed(): void;
      view: Window;
      avatarCache: UserAvatarCacheService | null;
    }
  ) {}

  update = () => {
    const active = new Set<string>();
    for (const [key, pointer] of this.input.pointers()) {
      const owner = this.input.owner(pointer.ownerUserId);
      const drawn = this.drawn.get(key);
      if (drawn?.pointer === pointer) {
        // The view moved: the pointer keeps going to the same object.
        const position = this.input.position(drawn.heading);
        if (!position) continue;
        active.add(key);
        drawn.owner = owner;
        if (moved(drawn.target, position)) this._go(drawn, position);
        continue;
      }
      // A new change: the pointer goes through what it changed, one after another.
      const route = pointer.targets.filter(
        target => this.input.position(target) !== null
      );
      const first = route.shift();
      if (!first) continue;
      active.add(key);
      if (drawn) {
        drawn.pointer = pointer;
        drawn.owner = owner;
        drawn.route = [first, ...route];
        drawn.done = false;
        this._next(drawn);
        continue;
      }
      const position = this.input.position(first)!;
      const added: DrawnAgentPointer = {
        pointer,
        owner,
        position,
        heading: first,
        target: position,
        route,
        stop: () => {},
        done: false,
        ended: false,
      };
      this.drawn.set(key, added);
      this._next(added);
    }
    for (const [key, previous] of this.drawn) {
      if (active.has(key)) continue;
      // An ended run's change can be drawn after its end (a database row
      // shard syncs later): its pointer still goes where the object is now.
      const position = this.input.position(previous.heading);
      if (position && moved(previous.target, position)) {
        if (previous.leave !== undefined) {
          this.input.view.clearTimeout(previous.leave);
          previous.leave = undefined;
        }
        previous.done = false;
        previous.ended = true;
        this._go(previous, position);
        continue;
      }
      if (previous.ended) continue;
      previous.ended = true;
      if (previous.done) this._leave(key, previous);
    }
    this.input.changed();
  };

  /** An ended run's pointer goes after standing at its last object a moment. */
  private _leave(key: string, drawn: DrawnAgentPointer) {
    drawn.leave = this.input.view.setTimeout(() => {
      if (this.drawn.get(key) !== drawn) return;
      drawn.stop();
      this.drawn.delete(key);
      this.input.changed();
    }, ENDED_RUN_POINTER_MS);
  }

  /** Goes on to the next object of its route that is on screen. */
  private _next(drawn: DrawnAgentPointer) {
    for (let next = drawn.route.shift(); next; next = drawn.route.shift()) {
      const position = this.input.position(next);
      if (!position) continue;
      drawn.heading = next;
      this._go(drawn, position);
      return;
    }
    drawn.done = true;
    if (drawn.ended && drawn.leave === undefined)
      for (const [key, value] of this.drawn)
        if (value === drawn) this._leave(key, drawn);
  }

  private _go(drawn: DrawnAgentPointer, position: AgentPointerPosition) {
    // A new way to the same object takes on what the pointer carries there.
    drawn.stop(drawn.carrying === drawn.heading);
    drawn.target = position;
    const from = drawn.position;
    const way = Math.hypot(position.x - from.x, position.y - from.y);
    const carry = this.input.carry?.(drawn.heading) ?? null;
    drawn.carrying = carry ? drawn.heading : undefined;
    const stop = moveAgentPointer({
      from,
      to: position,
      view: this.input.view,
      pixelsPerUnit: 1 / this.input.scale(),
      seed: `${drawn.pointer.runId}:${drawn.pointer.stepId}:${drawn.heading.id}`,
      moved: current => {
        drawn.position = current;
        carry?.moved(
          way > 0 ? Math.hypot(current.x - from.x, current.y - from.y) / way : 1
        );
        this.input.changed();
      },
      arrived: () => {
        carry?.arrived();
        this._next(drawn);
      },
    });
    drawn.stop = keepCarry => {
      stop();
      if (!keepCarry) carry?.arrived();
    };
  }

  dispose() {
    for (const pointer of this.drawn.values()) {
      pointer.stop();
      if (pointer.leave !== undefined)
        this.input.view.clearTimeout(pointer.leave);
    }
    this.drawn.clear();
  }

  render() {
    return repeat(
      this.drawn,
      ([key]) => key,
      ([key, { pointer, owner, position }]) => {
        const name = agentPointerLabel(pointer, owner?.name);
        const avatar =
          owner?.avatar && owner.id
            ? renderCachedUserAvatarUrl({
                avatarCache: this.input.avatarCache,
                avatarUrl: owner.avatar,
                userId: owner.id,
                render: (url, cached) =>
                  html`<img
                    src=${url}
                    alt=${owner.name}
                    style=${styleMap({
                      width: '28px',
                      height: '28px',
                      borderRadius: '50%',
                      objectFit: 'cover',
                    })}
                    @load=${() => revokeCachedUserAvatarObjectUrl(url, cached)}
                    @error=${() => revokeCachedUserAvatarObjectUrl(url, cached)}
                  />`,
              })
            : nothing;
        return html`<div
          data-testid="agent-pointer"
          data-agent-pointer=${key}
          data-run-id=${pointer.runId}
          data-owner-user-id=${pointer.ownerUserId}
          style=${styleMap({
            position: 'absolute',
            left: `${position.x}px`,
            top: `${position.y}px`,
            width: '24px',
            height: '24px',
            pointerEvents: 'none',
            transform: `translate(-50%, -50%) scale(${this.input.scale()})`,
            transformOrigin: 'center',
            zIndex: '3',
            color: cssVarV2('brand/primary'),
          })}
        >
          ${MultiCursorDuotoneIcon({ width: '24px', height: '24px' })}
          <div
            style=${styleMap({
              position: 'absolute',
              left: '24px',
              top: '0',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              padding: '2px 4px',
              borderRadius: '4px',
              whiteSpace: 'nowrap',
              fontSize: '12px',
              color: cssVarV2('text/primary'),
              backgroundColor: cssVarV2('layer/background/primary'),
              border: `1px solid ${cssVarV2('layer/insideBorder/border')}`,
            })}
          >
            ${avatar}<span>${name}</span>
          </div>
        </div>`;
      }
    );
  }
}
